import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Valence } from '@upright/common/model/types';
import { getAllEnumValues } from '@upright/common/utils';

// These values are saved in db, so if you change them, migrate the db values also.
export enum CompanyClientDataType {
  TARGET = 'target',
  INSIGHT = 'disclosure',
}

interface CompanyClientDatumBase<T extends CompanyClientDataType> {
  id: number;
  clientDataType: T;
  impactCategoryId: ImpactCategoryId;
  valence: Valence;
  relevance?: ClientDatumRelevance; // TODO: Once we clean this, change to mandatory
  companyId: string;
  text: string;
  link?: string;
  sourceName?: string;
  sourceLink?: string;
  sourceType?: ClientDatumSource;
  order?: number;
  liked?: boolean;
  likes?: number;
}

export type CompanyTargetDatum = CompanyClientDatumBase<CompanyClientDataType.TARGET>;

export interface CompanyInsightDatum extends CompanyClientDatumBase<CompanyClientDataType.INSIGHT> {
  magnitude: number;
}

export type CompanyClientDatum<T extends CompanyClientDataType = CompanyClientDataType> =
  T extends CompanyClientDataType.TARGET ? CompanyTargetDatum : CompanyInsightDatum;

export type CompanyClientDataset<T extends CompanyClientDataType = CompanyClientDataType> = {
  id: number;
  data?: Array<CompanyClientDatum<T>>;
  dataType: T;
  companyId: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  publicationRequestedAt?: string | null;
  publishedAt?: string | null;
  publishedBy?: number | null;
  createdBy?: number | null;
};

export interface ClientDataSecretToken {
  companyId: string;
  signature: string;
}

/**
 * There are 0-2 rows in dataset table per company and CompanyClientDataType. If there is one row, it's a "DRAFT". If there are two rows, one is "DRAFT" and other is "PUBLISHED".
 * When a user submits data for the first time, the draft gets a "reviewRequestedAt" timestamp, and is interpreted to be of state "REVIEW_REQUESTED".
 * On publish, the published dataset row gets created from the draft row, no matter in which state it was. The pusblished row has its "publishedAt" timestamp populated.
 * On unpublish, the published dataset row gets deleted.
 *
 * This enum does not cover all the different states the datasets can actually be in. The whole plethora of different states is written out in DashboardInsightsTargetsStatus in
 * company-dashboard-view/types.ts.
 */
export enum CompanyClientDatasetState {
  DRAFT = 'DRAFT',
  REVIEW_REQUESTED = 'REVIEW_REQUESTED',
  PUBLISHED = 'PUBLISHED',
}

export type InsightWithoutId = Omit<CompanyInsightDatum, 'id'>;
export type TargetWithoutId = Omit<CompanyTargetDatum, 'id'>;
export type CompanyClientDatumWithoutId<T extends CompanyClientDataType = CompanyClientDataType> =
  T extends CompanyClientDataType.TARGET ? TargetWithoutId : InsightWithoutId;

export enum Magnitude {
  NO_IMPACT = 0,
  MINOR = 1,
  MEDIUM = 2,
  MAJOR = 3,
}

export const allMagnitudes = getAllEnumValues(Magnitude, 'number');

export enum ClientDatumSource {
  UPRIGHT_COLLECTED = 'Data retrieved by Upright',
  COMPANY_SUBMITTED = 'Data provided by the company',
}

export enum ClientDatumRelevance {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High',
}

export function toNumericRelevance(r: ClientDatumRelevance | undefined) {
  switch (r) {
    case ClientDatumRelevance.HIGH:
      return 3;
    case ClientDatumRelevance.MEDIUM:
      return 2;
    case ClientDatumRelevance.LOW:
      return 1;
    default:
      return 2;
  }
}

export const allRelevances = Object.values(ClientDatumRelevance);
